import { recordIdentifierFor } from '@warp-drive/core';
import {
  createRecord as createRecordRequest,
  deleteRecord as deleteRecordRequest,
  findRecord as findRecordRequest,
  serializePatch,
  serializeResources,
  updateRecord as updateRecordRequest,
} from '@warp-drive/utilities/json-api';
import { buildBaseURL } from '@warp-drive/utilities';
import { pluralize } from '@warp-drive/utilities/string';
import { SkipCache } from '@warp-drive/core-types/request';
import { warn } from '@ember/debug';
import { SkipErrorReporting } from '../request-options.ts';
import { CONTRACT_HEADER } from '../handlers/type-normalization-handler.ts';
import type { Store } from '@warp-drive/core';
import type {
  ConstrainedRequestOptions,
  FindRecordOptions,
  RequestInfo,
} from '@warp-drive/core/types/request';

/**
 * Serializes cached record state for WarpDrive mutations; the underlying builders supply no body.
 * Optional ConstrainedRequestOptions pass through to the builder, including namespace overrides.
 * Bodies use schema-format names; TioTypeNormalizationHandler applies negotiated wire key format
 * and pluralized types.
 */

type AnyRecord = object;

/**
 * CREATE requests for these types use their v2 kit namespace and target contract. This bypasses
 * jsonapi-resources rewrites and camelizes dasherized response attributes for the cache; unlisted
 * types use root paths.
 */
const KIT_CREATE_NAMESPACES: Record<string, string> = {
  agreement: 'api/v2',
};

function applyKitContractHeader(init: object): void {
  const request = init as { headers?: Headers };
  const headers = new Headers(request.headers);
  headers.set(CONTRACT_HEADER, '2');
  request.headers = headers;
}

/**
 * {@link ConstrainedRequestOptions} plus the house-only `skipErrorReporting`
 * flag, for fire-and-forget saves whose failure must not touch the session.
 */
export interface SaveRecordOptions extends ConstrainedRequestOptions {
  skipErrorReporting?: boolean;
}

/**
 * Write the `SkipErrorReporting` opt-out onto an emitted request.
 *
 * The flag cannot travel inside the builder options: WarpDrive's
 * `ConstrainedRequestOptions` is a closed interface and the json-api builders
 * copy only the keys they know, so an extra key is dropped before it could
 * reach `request.options` — the only place `TioResponseHandler` looks. Callers
 * pass it as a save option; the helpers relocate it onto the emitted request.
 */
function applyErrorReportingOptOut(init: object, skipErrorReporting: boolean | undefined): void {
  if (!skipErrorReporting) {
    return;
  }
  // The mutation builders return closed literals (`CreateRequestOptions` /
  // `UpdateRequestOptions`) that don't declare `options`, even though the
  // request manager carries it through to the handlers — hence the widening.
  const request = init as { options?: Record<string, unknown> };
  request.options = { ...request.options, [SkipErrorReporting]: true };
}

/**
 * `JSON.stringify` replacer that THROWS on values it would otherwise silently
 * drop. `JSON.stringify` omits nested `function`/`symbol` values without error,
 * which would let `sanitizeAttrIfUnclonable` ship a lossy snapshot and "succeed"
 * with silent data loss. Throwing routes them through the same path as any other
 * unrepresentable value (leave the attribute untouched + warn in DEBUG) rather
 * than dropping data. BigInt and circular references already make `JSON.stringify`
 * throw natively.
 */
function throwOnUnserializableJsonValue(_key: string, value: unknown): unknown {
  const type = typeof value;
  if (type === 'function' || type === 'symbol') {
    throw new TypeError(`save-record: a ${type} value cannot be serialized as a JSON attribute`);
  }
  return value;
}

/**
 * Probes structuredClone and replaces rejected proxy attributes with a JSON snapshot. Cloneable
 * values remain untouched; setAttr compares against the remote baseline so a replacement stays
 * dirty.
 * Returns false only when cloning and JSON conversion both fail, such as functions, BigInt, or
 * cycles. Object values may be cloned again by serialization.
 */
function sanitizeAttrIfUnclonable(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>,
  key: string,
  value: unknown
): boolean {
  // Only objects can be unclonable here; primitives/null always clone.
  if (value === null || typeof value !== 'object') {
    return true;
  }
  try {
    structuredClone(value);
    return true;
  } catch (cloneError) {
    // Only a DataCloneError means "not structured-cloneable" — the expected
    // Glimmer reactive-proxy case (trackedObject / trackedArray). Any other
    // error is an unrelated bug we must not mask by JSON-snapshotting, so
    // rethrow it.
    if (!(cloneError instanceof Error) || cloneError.name !== 'DataCloneError') {
      throw cloneError;
    }
    // Swap the proxy for a plain JSON snapshot. Guard the round-trip itself: if
    // even JSON can't represent the value, leave it untouched and let the caller
    // warn in DEBUG so the real culprit is named, rather than trading the
    // DataCloneError for an opaque JSON.stringify crash deeper in serialization.
    try {
      store.cache.setAttr(
        identifier,
        key,
        JSON.parse(JSON.stringify(value, throwOnUnserializableJsonValue)) as never
      );
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Emit the DEBUG-only "attribute isn't serializable" warning. `warn` is stripped
 * in production builds, so this adds no shipping overhead.
 */
function warnUnserializableAttr(
  identifier: ReturnType<typeof recordIdentifierFor>,
  key: string
): void {
  warn(
    `save-record: attribute "${key}" on "${identifier.type}" is not serializable ` +
      `(a function, BigInt, or circular reference); the save will fail until it ` +
      `holds plain JSON.`,
    false,
    { id: 'tio-data.save-record.unserializable-attr' }
  );
}

/**
 * Make the attributes a `serializePatch` save will clone safe for
 * `structuredClone`. `serializePatch` clones ONLY the changed attributes
 * (`cache.changedAttrs`), so this sanitizes exactly that set — sanitizing more
 * would spuriously dirty unchanged attributes into the sparse PATCH body.
 */
function ensureChangedAttrsCloneable(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>
): void {
  if (!store.cache.hasChangedAttrs(identifier)) {
    return;
  }
  const changes = store.cache.changedAttrs(identifier);
  for (const key of Object.keys(changes)) {
    if (!sanitizeAttrIfUnclonable(store, identifier, key, changes[key]?.[1])) {
      warnUnserializableAttr(identifier, key);
    }
  }
}

/**
 * Ensure every serialized attribute is structuredClone-safe, including unchanged, default-filled,
 * or in-place-mutated values. serializeResources clones the whole resource.
 * Use cache.getAttr instead of cache.peek: peek clones first and would throw on the proxy being
 * repaired. Identity, relationship, and derived fields are excluded.
 */
function ensureAllAttrsCloneable(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>
): void {
  const fields = store.schema.fields(identifier);
  for (const field of fields.values()) {
    // The cache stores these kinds as JSON attributes (what serializeResources
    // clones); everything else is a relationship, identity, derived, or local
    // field that getAttr does not serve and the serializer does not clone.
    if (
      field.kind !== 'attribute' &&
      field.kind !== 'field' &&
      field.kind !== 'object' &&
      field.kind !== 'array' &&
      field.kind !== 'schema-object' &&
      field.kind !== 'schema-array'
    ) {
      continue;
    }
    const value = store.cache.getAttr(identifier, field.name);
    if (!sanitizeAttrIfUnclonable(store, identifier, field.name, value)) {
      warnUnserializableAttr(identifier, field.name);
    }
  }
}

export function saveNewRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: SaveRecordOptions
) {
  const identifier = recordIdentifierFor(record);
  // serializeResources clones the WHOLE resource, so sanitize every attribute.
  ensureAllAttrsCloneable(store, identifier);
  const { skipErrorReporting, ...builderOptions } = options ?? {};
  const kitNamespace = KIT_CREATE_NAMESPACES[identifier.type];
  const init = createRecordRequest(
    record as Parameters<typeof createRecordRequest>[0],
    kitNamespace ? { ...builderOptions, namespace: kitNamespace } : builderOptions
  );
  init.body = JSON.stringify(serializeResources(store.cache, identifier));
  if (kitNamespace) {
    applyKitContractHeader(init);
  }
  applyErrorReportingOptOut(init, skipErrorReporting);
  return store.request(init);
}

/**
 * POSTs a new record with write-only PaperTrail _changelog inside data.attributes. Serialize first,
 * then inject the value so schema-based serialization does not drop it.
 */
export function saveNewRecordWithChangelog<T extends AnyRecord>(
  store: Store,
  record: T,
  changelog: string,
  options?: ConstrainedRequestOptions
) {
  const identifier = recordIdentifierFor(record);
  // serializeResources clones the WHOLE resource, so sanitize every attribute.
  ensureAllAttrsCloneable(store, identifier);
  const init = createRecordRequest(record as Parameters<typeof createRecordRequest>[0], options);
  const body = serializeResources(store.cache, identifier);
  // Inject the changelog into the serialized body exactly as ApplicationSerializer did.
  const data = body.data as {
    type: string;
    id: string | null;
    attributes?: Record<string, unknown>;
  };
  data.attributes = data.attributes ?? {};
  data.attributes['_changelog'] = changelog;
  init.body = JSON.stringify(body);
  return store.request(init);
}

/**
 * Exclude hasMany linkage from record PATCHes. Child mutations can dirty inverse collections, and
 * JSONAPI::Resources interprets those arrays as authoritative replacements, unlinking omitted
 * children.
 * Manage collections through their own endpoints or explicit relationship builders; record saves
 * carry attributes and belongsTo linkage only.
 */
function stripHasManyRelationships(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>,
  patch: ReturnType<typeof serializePatch>
): void {
  const data = patch.data as { relationships?: Record<string, unknown> };
  const relationships = data.relationships;
  if (!relationships) {
    return;
  }
  const fields = store.schema.fields(identifier);
  for (const key of Object.keys(relationships)) {
    const kind = fields.get(key)?.kind;
    // 'hasMany' is the LegacyMode collection kind; 'collection' is its
    // PolarisMode equivalent. to-one kinds (belongsTo/resource) stay.
    if (kind === 'hasMany' || kind === 'collection') {
      delete relationships[key];
    }
  }
  if (Object.keys(relationships).length === 0) {
    delete data.relationships;
  }
}

export function saveExistingRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: SaveRecordOptions
) {
  const identifier = recordIdentifierFor(record);
  ensureChangedAttrsCloneable(store, identifier);
  const { skipErrorReporting, ...builderOptions } = options ?? {};
  // `patch: true`: the builder defaults to PUT, but the legacy
  // JSONAPIAdapter#updateRecord always sent PATCH — and the body here is a
  // sparse `serializePatch` document, which is PATCH semantics per JSON:API.
  const init = updateRecordRequest(record as Parameters<typeof updateRecordRequest>[0], {
    ...builderOptions,
    patch: true,
  });
  const patch = serializePatch(store.cache, identifier);
  stripHasManyRelationships(store, identifier, patch);
  init.body = JSON.stringify(patch);
  applyErrorReportingOptOut(init, skipErrorReporting);
  return store.request(init);
}

/** PATCHes an existing record with the write-only PaperTrail _changelog in data.attributes. */
export function saveExistingRecordWithChangelog<T extends AnyRecord>(
  store: Store,
  record: T,
  changelog: string,
  options?: ConstrainedRequestOptions
) {
  const identifier = recordIdentifierFor(record);
  ensureChangedAttrsCloneable(store, identifier);
  // PATCH, not the builder's PUT default — see saveExistingRecord.
  const init = updateRecordRequest(record as Parameters<typeof updateRecordRequest>[0], {
    ...options,
    patch: true,
  });
  const patch = serializePatch(store.cache, identifier);
  stripHasManyRelationships(store, identifier, patch);
  // Inject the changelog into the serialized body exactly as ApplicationSerializer did.
  const data = patch.data as {
    type: string;
    id: string | null;
    attributes?: Record<string, unknown>;
  };
  data.attributes = data.attributes ?? {};
  data.attributes['_changelog'] = changelog;
  init.body = JSON.stringify(patch);
  return store.request(init);
}

export function saveRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: SaveRecordOptions
) {
  const isNew = (record as { isNew?: boolean }).isNew;
  return isNew ? saveNewRecord(store, record, options) : saveExistingRecord(store, record, options);
}

export function deleteExistingRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
) {
  return store.request(
    deleteRecordRequest(record as Parameters<typeof deleteRecordRequest>[0], options)
  );
}

/**
 * Refetches by record identifier with reload: true, bypassing cache freshness. Updates the
 * materialized record in place and returns content.data.
 */
export function reloadRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  // `FindRecordOptions` (not `ConstrainedRequestOptions`) so callers can pass
  // `include` — the legacy `record.reload({ include })` refetched related
  // records, and `findRecordRequest` accepts `include` via `FindRecordOptions`.
  options?: FindRecordOptions
): Promise<T> {
  const { type, id } = recordIdentifierFor(record);
  // `findRecord` requires a concrete id; an unsaved record cannot be reloaded,
  // matching legacy `record.reload()` (which GETs the record's own URL).
  const init = findRecordRequest(type, id as string, { ...options, reload: true });
  return store.request(init).then((result) => (result.content as { data: T }).data);
}

/**
 * DELETEs an existing record and removes it from cache on success. The deleteRecord operation
 * drives willCommit/didCommit; no prior store.deleteRecord marking is needed.
 */
export function destroyRecordViaBuilder<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
): Promise<void> {
  const init = deleteRecordRequest(record as Parameters<typeof deleteRecordRequest>[0], options);
  return store.request(init).then(() => undefined);
}

/**
 * POSTs resource linkage to the owner URL plus /relationships/<pluralized-related-type>.
 * relatedRecords must be nonempty and homogeneous.
 * The { data: [{ type, id }] } body prepluralizes types because array linkage bypasses
 * TioTypeNormalizationHandler.
 */
export function addRelatedRecordsRequest<T extends AnyRecord, R extends AnyRecord>(
  store: Store,
  record: T,
  relatedRecords: R[],
  options?: ConstrainedRequestOptions
) {
  const first = relatedRecords[0];
  if (!first) {
    throw new Error('addRelatedRecordsRequest requires at least one related record');
  }
  const relatedType = pluralize(recordIdentifierFor(first).type);
  const owner = recordIdentifierFor(record);
  // Derive the (pluralized) owner URL via the `updateRecord` URL builder, then
  // append the relationship segment exactly as the legacy adapter did
  // (`${updateUrl}/relationships/${pluralize(relatedModelName)}`).
  const ownerUrl = buildBaseURL({
    op: 'updateRecord',
    identifier: { type: owner.type, id: owner.id as string },
    ...options,
  });
  // A bare RequestInfo (NOT the `updateRecord` builder's init): this is a
  // relationship-endpoint POST, not a commit of the owner resource, so it must
  // not carry the `op: 'updateRecord'` that would drive the owner through the
  // CacheHandler's commit lifecycle. Like the other action builders, it skips
  // the cache and returns the API's (empty) response unchanged.
  const init: RequestInfo = {
    url: `${ownerUrl}/relationships/${relatedType}`,
    method: 'POST',
    body: JSON.stringify({
      data: relatedRecords.map((related) => ({
        type: relatedType,
        id: recordIdentifierFor(related).id,
      })),
    }),
    cacheOptions: { [SkipCache]: true },
  };
  return store.request(init);
}

/**
 * PATCHes one belongsTo linkage at the owner URL plus /relationships/<relationName>. The relation
 * name is used verbatim, while the owner resource type is pluralized.
 * The body is a single { data: { type, id } } object. TioTypeNormalizationHandler applies
 * negotiated type casing; unlike array linkage, this object is normalized by the handler.
 * @param relationName belongsTo name used unchanged in the URL
 * @param relatedRecord supplies the linked resource identity
 */
export function setRelatedRecordRequest<T extends AnyRecord, R extends AnyRecord>(
  store: Store,
  record: T,
  relationName: string,
  relatedRecord: R,
  options?: ConstrainedRequestOptions
) {
  const owner = recordIdentifierFor(record);
  const relatedType = pluralize(recordIdentifierFor(relatedRecord).type);
  // Derive the PLURALIZED owner URL, then append the relationship segment with
  // the VERBATIM relation name exactly as ApplicationAdapter#urlForUpdateRecord
  // did (`${originalEndpoint}/relationships/${adapterOptions.updateRelationship}`,
  // where `originalEndpoint` is the legacy `buildURL`'s PLURAL owner path, e.g.
  // `/companies/9`, `/financial-institutions/9`).
  // NB: `buildBaseURL` does NOT pluralize on its own — when no `resourcePath` is
  // given it falls back to the RAW `identifier.type` (see
  // @warp-drive/utilities `resourcePathForType`, which returns `identifier.type`
  // verbatim). Only the `updateRecord()` / `createRecord()` builder WRAPPERS set
  // `resourcePath: pluralize(camelize(type))`. We replicate the dasherized-plural
  // owner path (camelize would WRONGLY camelCase `financial-institution`) by
  // setting `resourcePath` to `pluralize(owner.type)` — `owner.type` is already
  // the dasherized schema type, and `pluralize` alone preserves the dashes
  // (`financial-institution` -> `financial-institutions`).
  const ownerUrl = buildBaseURL({
    op: 'updateRecord',
    identifier: { type: owner.type, id: owner.id as string },
    resourcePath: pluralize(owner.type),
    ...options,
  });
  // A bare RequestInfo (NOT the `updateRecord` builder's init): this is a
  // relationship-endpoint PATCH, not a commit of the owner resource, so it must
  // not carry `op: 'updateRecord'` (which would drive the owner through the
  // CacheHandler's commit lifecycle). It skips the cache and returns the API's
  // (empty) response unchanged.
  const init: RequestInfo = {
    url: `${ownerUrl}/relationships/${relationName}`,
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: relatedType,
        id: recordIdentifierFor(relatedRecord).id,
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
  return store.request(init);
}

/**
 * PATCHes the full hasMany set at /<owner-plural>/:id/relationships/<relationshipName>; an empty
 * array clears it. The relationship name is used verbatim.
 * Array linkage bypasses TioTypeNormalizationHandler, so each resource type is prepluralized here.
 * @param relatedRecords complete desired set, not additions
 */
export function replaceRelatedRecordsRequest<T extends AnyRecord, R extends AnyRecord>(
  store: Store,
  record: T,
  relationshipName: string,
  relatedRecords: R[],
  options?: ConstrainedRequestOptions
) {
  const owner = recordIdentifierFor(record);
  const ownerUrl = buildBaseURL({
    op: 'updateRecord',
    identifier: { type: owner.type, id: owner.id as string },
    resourcePath: pluralize(owner.type),
    ...options,
  });
  const init: RequestInfo = {
    url: `${ownerUrl}/relationships/${relationshipName}`,
    method: 'PATCH',
    body: JSON.stringify({
      data: relatedRecords.map((related) => {
        const { type, id } = recordIdentifierFor(related);
        if (!id)
          throw new Error(
            `replaceRelatedRecordsRequest: unsaved record (type '${type}') has no id`
          );
        return { type: pluralize(type), id };
      }),
    }),
    cacheOptions: { [SkipCache]: true },
  };
  return store.request(init);
}
